import React from 'react';
import { Navigate, useNavigate } from 'react-router';

import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import { hentGyldigSkiltForKjoretoy } from '../../utils/api';
import {
    skiltbestillingsteg,
    useSkiltbestillingState,
} from './Skiltbestilling';
import { Stegveiviser } from '../../components/Stegveiviser';
import { Skiltvisning } from './internals/Skiltvisning';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { useEffectOnce } from '../../hooks/useEffectOnce';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import {
    SVVButton,
    SVVButtonCancel,
    SVVButtonRow,
} from '@komponentkassen/svv-button';
import { useForm } from 'react-hook-form';

export const VisSkiltstorrelse: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    const api = useApi(hentGyldigSkiltForKjoretoy);
    const { handleSubmit } = useForm();
    const rettighetId = useSkiltbestillingState((state) => state.rettighetId);
    const kjennemerke = useSkiltbestillingState((state) => state.kjennemerke);
    const tegnkombinasjon = useSkiltbestillingState(
        (state) => state.tegnkombinasjon,
    );
    const setKjennemerkestorrelser = useSkiltbestillingState(
        (state) => state.setKjennemerkestorrelser,
    );
    const setKjennemerketype = useSkiltbestillingState(
        (state) => state.setKjennemerketype,
    );
    const setKjennemerke = useSkiltbestillingState(
        (state) => state.setKjennemerke,
    );

    useEffectOnce(() => {
        api.hent(rettighetId, kjennemerke);
    });

    if (!rettighetId) {
        return <Navigate to="/" />;
    }

    const visResultat = api.data?.ok;

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1>{t('personlig-kjennemerke.bestill.overskrift')}</h1>
            </SVVLayoutBanner>

            <SVVLayoutBanner secondary>
                <form
                    className="form-group"
                    onSubmit={handleSubmit(() => {
                        if (visResultat) {
                            setKjennemerkestorrelser({
                                foran: api.data!.kjennemerkestorrelseForan,
                                bak: api.data!.kjennemerkestorrelseBak,
                            });
                            setKjennemerketype(api.data!.kjennemerkeType);
                            navigate('/skiltbestilling/hentested');
                        }
                    })}
                >
                    <div className="form-layout responsive-spacing-bottom--xxsmall">
                        <div className="form-layout__nav">
                            <Stegveiviser
                                steg={skiltbestillingsteg}
                                aktivtSteg={2}
                            />
                        </div>
                        <div className="form-layout__main">
                            <h2 className="spacing-bottom--xsmall">
                                {t(
                                    'personlig-kjennemerke.bestill.steg.skilttype',
                                )}
                            </h2>
                            <p className="spacing-bottom--small">
                                {t(
                                    'personlig-kjennemerke.bestill.storrelse.illustrasjon',
                                )}
                            </p>
                            <div aria-live="polite">
                                {api.laster && (
                                    <SVVSpinner size="small" centered={false}>
                                        {t('personlig-kjennemerke.delt.laster')}
                                    </SVVSpinner>
                                )}
                            </div>

                            {visResultat && (
                                <>
                                    <p className="svv-h5">
                                        {t(
                                            'personlig-kjennemerke.bestill.oppsummering.personlig-skilt',
                                        )}
                                        :
                                    </p>
                                    <p className="spacing-bottom--medium">
                                        {tegnkombinasjon}
                                    </p>
                                    <h3 className="spacing-bottom--xsmall">
                                        {t(
                                            'personlig-kjennemerke.bestill.forhandsvisning.overskrift',
                                        )}
                                        :
                                    </h3>
                                    <Skiltvisning
                                        modus="detaljer"
                                        kjennemerkestorrelser={{
                                            foran: api.data!
                                                .kjennemerkestorrelseForan,
                                            bak: api.data!
                                                .kjennemerkestorrelseBak,
                                        }}
                                        kjennemerketype={
                                            api.data!.kjennemerkeType
                                        }
                                        tegnkombinasjon={
                                            api.data!.tegnkombinasjon
                                        }
                                    />
                                </>
                            )}
                            <div className="svv-knapperad--spacing-top">
                                <SVVButtonRow>
                                    <SVVButton type="submit">
                                        {t('personlig-kjennemerke.delt.neste')}
                                    </SVVButton>
                                    <SVVButton
                                        onClick={() => {
                                            navigate(-1);
                                            setKjennemerke('');
                                        }}
                                        color="secondary"
                                    >
                                        {t(
                                            'personlig-kjennemerke.delt.tilbake',
                                        )}
                                    </SVVButton>
                                    <SVVButtonCancel
                                        href={`#status/${rettighetId}`}
                                    >
                                        {t('personlig-kjennemerke.delt.avbryt')}
                                    </SVVButtonCancel>
                                </SVVButtonRow>
                            </div>
                        </div>
                    </div>
                </form>
            </SVVLayoutBanner>
        </>
    );
};
